import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import API from "../../../services/api";
import "./timetable.css";

export default function TimetableView() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const school = JSON.parse(localStorage.getItem("schoolAdmin"))?.school;

  /* ===================== STATES ===================== */
  const [dayId, setDayId] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [periods, setPeriods] = useState([]);

  const [form, setForm] = useState({
    period_number: "",
    start_time: "",
    end_time: "",
    subject: "",
    teacher_id: "",
  });

  /* ===================== ROUTE STATE ===================== */
  const class_id = state?.class_id;
  const section_id = state?.section_id;
  const day = state?.day;
  const session = state?.session;

  const isInvalid =
    !school?.id || !class_id || !section_id || !day || !session;

  /* ===================== CREATE / GET WEEKLY DAY ===================== */
  useEffect(() => {
    if (isInvalid) return;

    const createWeeklyDay = async () => {
      try {
        const res = await API.post(
          "/school-admin/weekly-timetable/day/create/",
          {
            class_id,
            section_id,
            day,
            session,
          },
          { headers: { "X-SCHOOL-ID": school.id } }
        );

        setDayId(res.data.day_id);
      } catch (err) {
        alert("Failed to create weekly timetable day");
      }
    };

    createWeeklyDay();
  }, [class_id, section_id, day, session, school?.id, isInvalid]);

  /* ===================== LOAD TEACHERS ===================== */
  useEffect(() => {
    if (!school?.id) return;

    API.get("/school-admin/teachers/", {
      headers: { "X-SCHOOL-ID": school.id },
    }).then((res) => {
      setTeachers(res.data.teachers || []);
    });
  }, [school?.id]);

  /* ===================== LOAD SUBJECTS ===================== */
  useEffect(() => {
    if (!class_id || !school?.id) return;

    API.get(`/school-admin/subjects/?class_id=${class_id}`, {
      headers: { "X-SCHOOL-ID": school.id },
    }).then((res) => {
      setSubjects(res.data.subjects || []);
    });
  }, [class_id, school?.id]);

  /* ===================== INVALID GUARD ===================== */
  if (isInvalid) {
    return (
      <div className="tt-page">
        <h2 className="tt-title">Invalid Timetable Request</h2>
        <button
          className="tt-btn"
          onClick={() => navigate("/school-admin/timetable")}
        >
          Go Back
        </button>
      </div>
    );
  }

  /* ===================== ADD PERIOD ===================== */
  const addPeriod = async () => {
    if (!dayId) return alert("Timetable day not ready");

    if (
      !form.period_number ||
      !form.start_time ||
      !form.end_time ||
      !form.subject
    ) {
      return alert("Please fill all required fields");
    }

    await API.post(
      "/school-admin/weekly-timetable/period/add/",
      {
        day_id: dayId,
        period_number: Number(form.period_number),
        start_time: form.start_time,
        end_time: form.end_time,
        subject: form.subject,
        teacher_id: form.teacher_id || null,
      },
      { headers: { "X-SCHOOL-ID": school.id } }
    );

    setPeriods([...periods, form]);

    setForm({
      period_number: "",
      start_time: "",
      end_time: "",
      subject: "",
      teacher_id: "",
    });
  };

  /* ===================== UI ===================== */
  return (
    <div className="tt-page">
      <h2 className="tt-title">
        Weekly Timetable – {day.toUpperCase()} ({session})
      </h2>

      <div className="tt-form">
        {/* PERIOD NUMBER */}
        <input
          type="number"
          min="1"
          placeholder="Period No"
          value={form.period_number}
          onChange={(e) =>
            setForm({ ...form, period_number: e.target.value })
          }
        />

        {/* TIME */}
        <input
          type="time"
          value={form.start_time}
          onChange={(e) =>
            setForm({ ...form, start_time: e.target.value })
          }
        />

        <input
          type="time"
          value={form.end_time}
          onChange={(e) =>
            setForm({ ...form, end_time: e.target.value })
          }
        />

        {/* SUBJECT */}
        <select
          value={form.subject}
          onChange={(e) =>
            setForm({ ...form, subject: e.target.value })
          }
        >
          <option value="">Select Subject</option>
          {subjects.map((s) => (
            <option key={s.id} value={s.name}>
              {s.name}
            </option>
          ))}
        </select>

        {/* TEACHER */}
        <select
          value={form.teacher_id}
          onChange={(e) =>
            setForm({ ...form, teacher_id: e.target.value })
          }
        >
          <option value="">Select Teacher</option>
          {teachers.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>

        <button className="tt-btn" onClick={addPeriod}>
          Add Period
        </button>
      </div>

      {/* PREVIEW */}
      <div className="tt-preview">
        {periods.map((p, i) => (
          <div key={i} className="tt-row">
            <b>P{p.period_number}</b> — {p.subject}
          </div>
        ))}
      </div>
    </div>
  );
}